import type { PrintContext } from '@ahd/shared/types/ahd-cars-print/context';
import type { Car } from '@ahd/shared/types/ahd-cars/car/car';
import type { Firma } from '@ahd/shared/types/ahd-cars/firma/firma';
import type { FirmaUser } from '@ahd/shared/types/ahd-cars/firma/user';
import { startOfDay } from 'date-fns/startOfDay';
import sFarbschema from '../_json/s_farbschema';
import sProfile, { type Profil, type Rolle } from '../_json/s_profile';
import { type UserSettings } from '../login/EnsureLoggedInContainer';
import { LEAD } from '../vars';
import { urlImagesRelease } from './AxiosVars';
import { formatDate, isAfter, isBefore } from './Date';
import { checkUndefined, checkUndefinedNullOrEmpty, checkUndefinedOrNull } from './Tools';
import { type Vertrage, getVertrag } from './Vertrag';
import type { SelectDataObject } from './inputfields/Select';
import type { ArrayElement } from './utils';

export type Session = {
    allowDAT?: boolean;
    btob?: Pick<
        Firma,
        | 'al'
        | 'c'
        | 'color'
        | 'do'
        | 'f'
        | 'g'
        | 'h'
        | 'hm'
        | 'i'
        | 'lat'
        | 'latitude'
        | 'lng'
        | 'longitude'
        | 'm'
        | 'mt'
        | 'n'
        | 'o'
        | 'openMoFr'
        | 'openSa'
        | 'p'
        | 'rg'
        | 'rgt'
        | 'rn'
        | 's'
        | 'sh'
        | 't'
        | 'typ'
        | 'uid'
        | 'usr'
        | 'usrNames'
    >[];
    btob_clients?: Pick<
        Firma,
        | 'al'
        | 'c'
        | 'color'
        | 'do'
        | 'f'
        | 'g'
        | 'h'
        | 'hm'
        | 'i'
        | 'lat'
        | 'latitude'
        | 'lng'
        | 'longitude'
        | 'm'
        | 'mt'
        | 'n'
        | 'o'
        | 'openMoFr'
        | 'openSa'
        | 'p'
        | 'rg'
        | 'rn'
        | 's'
        | 'sh'
        | 't'
        | 'typ'
        | 'uid'
        | 'usr'
        | 'usrNames'
    >[];
    btob_switch?: Firma['i'][];
    conf?: UserSettings;
    eMailMymo?: string;
    eMailMymoTo?: string;
    features?: any[];
    firma?: Pick<
        Firma,
        | 'al'
        | 'bm'
        | 'c'
        | 'cfg'
        | 'color'
        | 'do'
        | 'f'
        | 'g'
        | 'h'
        | 'hm'
        | 'i'
        | 'ih'
        | 'lat'
        | 'latitude'
        | 'lng'
        | 'longitude'
        | 'm'
        | 'mt'
        | 'n'
        | 'o'
        | 'openMoFr'
        | 'openSa'
        | 'openMoFrService'
        | 'openSaService'
        | 'openMoFrWerkstatt'
        | 'openSaWerkstatt'
        | 'p'
        | 'prio'
        | 'rg'
        | 'rn'
        | 's'
        | 'sh'
        | 't'
        | 'typ'
        | 'u'
        | 'uid'
        | 'usr'
        | 'usrNames'
        | 'v'
    >;
    gDebug?: boolean;
    gFirmaAdrPzOrt?: string;
    gFirmaAdrStrasseHnr?: string;
    gFirmaName?: string;
    gFirmaShort?: string;
    gIdFirma?: number;
    gIdUser?: number;
    gIsAdmin?: boolean;
    gIsLeadMaster?: boolean;
    gIsSupport?: boolean;
    gIsVKL?: boolean;
    gLoadGruppe?: boolean;
    gUserName?: string;
    header?: string;
    idTel?: number;
    idStandortUser?: number;
    isMobileRating?: boolean;
    isPortalAutoCRM?: boolean;
    isPushMsg?: boolean;
    isTestPhase?: boolean;
    leadDisable?: boolean;
    mitarbeiter?: {
        standort?: string;
        user: {
            [index: number]: string;
            selected: 'true' | 'false';
        }[];
    }[];
    mwst?: number;
    nomailmaster?: boolean;
    portals?: number[];
    portalsAll?: number[];
    portalsLead?: number[];
    printFirma?: PrintContext['firma'];
    rollen?: string[];
    selKontaktereignis?: any[];
    selPrint?: {
        [index: `${number}`]: string;
        link: string;
        selected: boolean;
    }[];
    session?: string;
    sorte?: any[];
    texte?: any[];
    token?: string;
    vertretungSeit?: number;
    vertretungUser?: number;
};

export const testphasen: { [index: keyof Vertrage | string]: boolean } = {
    uwe: true,
    hp_alt: true,
    hp_gruppe: true,
    json: true,
    hp_kp2: true,
    tracking: true,
    akte: true,
    carscanner: true,
    inz: true,
    lead: true,
    individ: true,
};
export let gSession: Session = {};
export const mViews: Profil = {};

export function getSession() {
    return gSession;
}

export function setSession(s?: Session) {
    if (!checkUndefinedNullOrEmpty(s)) {
        gSession = s;
        window.sessionStorage.setItem(getSessionName(), JSON.stringify(s));
        window.sessionStorage.setItem(getTokenName(), s.token);
        setMwSt();
    } else clearSession();
}

export function getSessionHeader() {
    return gSession.header;
}

export function getPrintFirma() {
    return gSession.printFirma;
}

export function setPrintFirma(firma: Session['printFirma']) {
    gSession.printFirma = firma;
}

export function getMwSt(decimal?: boolean) {
    return decimal ? 1 + gSession.mwst / 100 : gSession.mwst;
}

export function setMwSt() {
    const today = new Date();
    if (isBefore(today, startOfDay(new Date('2021-01-01'))) && isAfter(today, startOfDay(new Date('2020-07-01')))) gSession.mwst = 16;
    else gSession.mwst = 19;
}

export function getConf(): UserSettings {
    return !checkUndefinedNullOrEmpty(gSession?.conf) ? gSession.conf : undefined;
}

export function setConf(newConf: UserSettings) {
    gSession.conf = newConf;
    setSession(gSession);
}

export function getDatumZeit(datum) {
    if (datum == undefined) {
        return undefined;
    } else {
        return 0 == datum ? undefined : formatDate(datum, 'dd.MM.yyyy HH:mm:ss');
    }
}

export function getDatum(datum) {
    if (datum == undefined) {
        return undefined;
    } else {
        return 0 == datum ? undefined : formatDate(datum, 'dd.MM.yyyy');
    }
}

export function getDatum4Input(datum) {
    if (datum == undefined) {
        return undefined;
    } else {
        return 0 == datum ? undefined : formatDate(datum, 'yyyy-MM-dd');
    }
}

export function getDatumFirefox(strDatum) {
    if (strDatum != undefined && strDatum != 0) {
        const parts = strDatum.split('.');
        if (parts.length == 3) {
            strDatum = parts[2] + '-' + parts[1] + '-' + parts[0];
            return strDatum;
        }
        return strDatum;
    }
}

export function getTime(strDatum) {
    if (strDatum != undefined && strDatum != 0) {
        const parts = strDatum.split('.');
        if (parts.length == 3) {
            strDatum = parts[2] + '-' + parts[1] + '-' + parts[0];
            return new Date(strDatum).getTime();
        }
    }
    return new Date(strDatum).getTime();
}

export function clearSession() {
    window.sessionStorage.clear();
    gSession = {};
}

// String
export function getRoute(to) {
    if (checkUndefinedNullOrEmpty(to) || to === '/login' || to === '/loginr' || to === '/logout') return '/dashboard';
    return to;
}

// Integer
export function getIdFirma(): Session['gIdFirma'] {
    return gSession.gIdFirma;
}

// Integer
export function getIdStandort() {
    return !checkUndefinedNullOrEmpty(gSession.idStandortUser) && gSession.idStandortUser > 0 ? gSession.idStandortUser : gSession.gIdFirma;
}

export function isMobileRating() {
    return gSession.isMobileRating || false;
}

export function isHondaFugel() {
    const id = gSession.gIdFirma;
    if (id === 53240) {
        return true;
    } else if (id === 53270) {
        return true;
    } else if (id === 53280) {
        return true;
    } else if (id === 53290) {
        return true;
    } else if (id === 53300) {
        return true;
    }
    return false;
}

export function isBlockAmRing() {
    const id = gSession.gIdFirma;
    if (id === 53900) {
        return true;
    } else if (id === 53970) {
        return true;
    } else if (id === 53980) {
        return true;
    } else if (id === 53990) {
        return true;
    } else if (id === 54000) {
        return true;
    } else if (id === 54010) {
        return true;
    } else if (id === 54020) {
        return true;
    } else if (id === 54564) {
        return true;
    }
    return false;
}

export function isAHSorg() {
    const id = gSession.gIdFirma;
    return id === 52410;
}

export function isAHPoser() {
    const id = gSession.gIdFirma;
    return id === 50524;
}

export function isAHLiliensiek() {
    const id = gSession.gIdFirma;
    return id === 13890;
}

export function isAHKuhnWitte() {
    const id = gSession.gIdFirma;
    const array = [38229, 52930, 53950];
    return array.includes(id);
}

export function isAHMuellerBertMueller() {
    const id = gSession.gIdFirma;
    const array = [19039, 49192];
    return array.includes(id);
}

export function isAHGloecknerDD() {
    const id = gSession.gIdFirma;
    const array = [54290, 54300];
    return array.includes(id);
}

export function isAHOstmann() {
    const id = gSession.gIdFirma;
    return id === 49282;
}

export function isACAltmittweida() {
    const id = gSession.gIdFirma;
    return id === 54593;
}

export function isImport2EVA() {
    const id = gSession.gIdFirma;
    const array = [
        9100, 9800, 10370, 10410, 10730, 10750, 10930, 10970, 11630, 11940, 11970, 12370, 48169, 48210, 48861, 49042, 51460, 51690, 52800, 52960, 53760, 53960, 54040, 54050, 54170, 54310, 54320, 54410,
        54565, 54792, 54799
    ];
    return array.includes(id);
}

export function isTester() {
    const id = gSession.gIdFirma;
    return id < 10000;
}

export function isProgrammierung() {
    const id = gSession.gIdFirma;
    return id === 9100 || id === 9300 || id === 9800;
}

export function isNotTester() {
    const id = gSession.gIdFirma;
    return id > 10000;
}

// String
export function getToken() {
    return gSession.token;
}

// Integer
export function getIdUser() {
    return gSession.gIdUser;
}

// Bool
export function isOwnLead(users) {
    // return (users && parseInt(users[users.length - 1]) === getIdUser());

    if (users) {
        for (let i = 0; i < users.length; i++) {
            const idU = users[i];
            if (parseInt(idU) === getIdUser()) {
                return true;
            }
        }
    }
    return false;
}

export function getUserName() {
    return gSession.gUserName;
}

export function setupCrisp(session) {
    try {
        // Firma
        const firma = session.firma || {};
        // Mitarbeiter
        let mitarbeiter = undefined;
        let avatar = [0, 0];
        if (firma.usr) {
            for (let i = 0; i < firma.usr.length; i++) {
                const m = firma.usr[i];
                if (parseInt(session.gIdUser) == m.i) {
                    mitarbeiter = m;
                    avatar = [firma.i, m.i];
                }
            }
        }
        if (session.btob_clients) {
            for (let i = 0; i < session.btob_clients.length; i++) {
                const b = session.btob_clients[i];
                for (let j = 0; j < b.usr.length; j++) {
                    const m = b.usr[j];
                    if (parseInt(session.gIdUser) == m.i) {
                        mitarbeiter = m;
                        avatar = [b.i, m.i];
                    }
                }
            }
        }
        const crispObj = {
            name: mitarbeiter ? mitarbeiter.vn + ' ' + mitarbeiter.nn + ' (' + mitarbeiter.i + ')' : '',
            mail: mitarbeiter && mitarbeiter.m ? mitarbeiter.m : '',
            phone: mitarbeiter && mitarbeiter.t ? mitarbeiter.t : '',
            avatar: avatar[0] !== 0 && avatar[1] !== 0 ? urlImagesRelease + '/s/v1/firma/' + avatar[0] + '/' + avatar[1] + '/user.jpg' : undefined,
            firmaName: firma.n ? firma.n + ' (' + firma.i + ')' : '',
            firmaId: firma.i ? firma.i : '',
            firmaStandort: {
                land: firma.c ? firma.c : '',
                stadt: firma.o || '',
            },
        };

        window.$crisp = [];
        if (crispObj.mail) window.$crisp.push(['set', 'user:email', [crispObj.mail]]);
        if (crispObj.phone) window.$crisp.push(['set', 'user:phone', [crispObj.phone]]);
        if (crispObj.name) window.$crisp.push(['set', 'user:nickname', [crispObj.name]]);
        if (crispObj.avatar) window.$crisp.push(['set', 'user:avatar', [crispObj.avatar]]);
        const firmenArray = [];
        if (crispObj.firmaName) firmenArray.push(crispObj.firmaName);
        if (crispObj.firmaId) firmenArray.push({ url: 'https://cars-support.autohaus-digital.de/firmen/client/' + crispObj.firmaId });
        if (crispObj.firmaStandort && crispObj.firmaStandort.land && crispObj.firmaStandort.stadt) firmenArray.push({ geolocation: [crispObj.firmaStandort.land, crispObj.firmaStandort.stadt] });
        if (!checkUndefinedNullOrEmpty(firmenArray)) window.$crisp.push(['set', 'user:company', firmenArray]);
    } catch (err: any) {
        console.error(err);
    }
}

export function getUserMail() {
    const mitarbeiter = getMitarbeiter4Id(getIdUser());
    if (mitarbeiter) return mitarbeiter.m;
}

export function getUserPhone() {
    const mitarbeiter = getMitarbeiter4Id(getIdUser());
    if (mitarbeiter) return mitarbeiter.t;
}

export function getUserImage(paramUser?, crisp?, ts?) {
    if (!LEAD && Array.isArray(paramUser)) {
        if (paramUser[0] !== 0 && paramUser[1] !== 0) return urlImagesRelease + '/s/v1/firma/' + paramUser[0] + '/' + paramUser[1] + '/user.jpg' + (ts ? '?ts=' + ts : '');
    } else {
        if (checkUndefined(gSession) || checkUndefined(gSession.firma) || checkUndefined(gSession.firma.usr)) return undefined;
        let user = [0, 0];
        for (let i = 0; i < gSession.firma.usr.length; i++) {
            const m = gSession.firma.usr[i];
            if ((!checkUndefinedNullOrEmpty(paramUser) ? paramUser : getIdUser()) == m.i) user = [gSession.firma.i, m.i];
        }
        // B2B prüfen
        if ((user[0] === 0 || user[1] === 0) && getBtoBClients()) {
            for (let i = 0; i < getBtoBClients().length; i++) {
                const b = getBtoBClients()[i];
                for (let j = 0; j < b.usr.length; j++) {
                    const m = b.usr[j];
                    if ((!checkUndefinedNullOrEmpty(paramUser) ? paramUser : getIdUser()) == m.i) user = [b.i, m.i];
                }
            }
        }
        if (user[0] !== 0 && user[1] !== 0) return urlImagesRelease + '/s/v1/firma/' + user[0] + '/' + user[1] + '/user.jpg' + (ts ? '?ts=' + ts : '');
        return crisp ? '' : undefined;
    }
}

export function getSwitchUsers() {
    if (!checkUndefined(gSession) && !checkUndefinedNullOrEmpty(gSession.btob_clients) && !checkUndefinedNullOrEmpty(gSession.btob_switch)) {
        const firmen = [];
        gSession.btob_switch.map((userId) => {
            const firmenArray = getBtoBClients();
            firmenArray.map((firma) => {
                for (let i = 0; i < firma.usr.length; i++) {
                    if (firma.usr[i].i === userId) firmen.push({ id: userId, firmaName: firma.n, alias: firma.al });
                }
            });
        });
        firmen.sort((a, b) => (a.alias || a.firmaName || '').toLowerCase().localeCompare((b.alias || b.firmaName || '').toLowerCase()));
        return firmen;
    }
    return undefined;
}

export function getAllPortals() {
    if (checkUndefined(gSession) || checkUndefined(gSession.portalsAll)) return [];
    else return gSession.portalsAll;
}

export function getAllLeadPortals() {
    if (checkUndefined(gSession) || checkUndefined(gSession.portalsLead)) return [];
    else return gSession.portalsLead;
}

export function getActivePortals() {
    if (checkUndefined(gSession) || checkUndefinedNullOrEmpty(gSession.portals)) return [];
    else return gSession.portals;
}

export function getFirmaName() {
    return gSession.gFirmaName;
}

export function getFarbeFirma() {
    return gSession && gSession.firma && gSession.firma.color ? (!checkUndefinedNullOrEmpty(sFarbschema[gSession.firma.color]) ? gSession.firma.color : 0) : 0;
}

export function getFirmaAlias() {
    return gSession.firma && gSession.firma.al ? gSession.firma.al : gSession.gFirmaName;
}

export function getFirma() {
    return gSession.firma;
}

export function getFirma4Id(idFirma: Firma['i']): ArrayElement<Session['btob']> | Session['firma'] {
    if (idFirma == getIdFirma()) {
        return getFirma();
    }
    if (!isBtoB()) {
        return undefined;
    }
    for (let i = 0; i < getBtoB().length; i++) {
        if (idFirma == getBtoB()[i].i) {
            return getBtoB()[i];
        }
    }
    return undefined;
}

export function getTexte() {
    return gSession.texte;
}

export function getTextvorlage(key) {
    const option = gSession.texte[key];
    if (checkUndefined(option)) {
        return;
    }
    return option[Object.keys(option)[0]];
}

export function isVKL() {
    return gSession.gIsVKL;
}

export function isVK() {
    return isProfil(getIdUser(), 'Verkäufer');
}

export function isDebug() {
    return gSession.gDebug;
}

export function isAdmin() {
    return gSession.gIsAdmin;
}

export function isLeadMaster() {
    return gSession.gIsLeadMaster || isRolle('eMail-Master');
}

export function isBildbearbeiter() {
    return isProfil(getIdUser(), 'Bildbearbeiter');
}

export function isInternetverantwortlicher() {
    return isProfil(getIdUser(), 'Internetverantwortlicher');
}

export function isDisposition() {
    return isProfil(getIdUser(), 'Disposition');
}

export function isDatenexport() {
    return isProfil(getIdUser(), 'Datenexport');
}

export function isWerkstatt() {
    return isProfil(getIdUser(), 'Werkstatt');
}

export function isLackierung() {
    return isProfil(getIdUser(), 'Lackierung');
}

export function isAufbereitung() {
    return isProfil(getIdUser(), 'Aufbereitung');
}

export function isProfil(idUser: number, strProfil: Rolle) {
    const m = getMitarbeiter4Id(idUser);
    if (checkUndefinedOrNull(m) || checkUndefinedOrNull(m.p)) {
        return false;
    }
    return m.p && m.p.includes(strProfil);
}

export function getMitarbeiter() {
    return gSession.mitarbeiter;
}

export function getMitarbeiterForSelect(idLoc?: number, sendSub?: boolean): SelectDataObject[] {
    if (gSession == undefined || gSession.firma == undefined || gSession.firma.usr == undefined) return undefined;
    const idStandort = idLoc ? idLoc : -1;
    const mitarbeiter: any = [{ i: -1, t: '(alle)' }];

    if (idStandort === -1 || idStandort === getIdFirma()) {
        for (let i = 0; i < gSession.firma.usr.length; i++) {
            const userObj = gSession.firma.usr[i];
            const id = userObj && userObj.i;
            const sub = userObj && userObj.s;
            const name = userObj && [userObj.nn, userObj.vn].join(', ');
            const valid = userObj && userObj.v;
            const firma = (gSession.firma && gSession.firma.al) || (gSession.firma && gSession.firma.n);
            if (valid && sub !== 900 && !name.includes('CarQR') && !name.includes('ftp')) mitarbeiter.push({ i: sendSub ? sub : id, t: name, group: firma });
        }
    }
    // B2B prüfen
    if (getBtoBClients()) {
        for (let i = 0; i < getBtoBClients().length; i++) {
            const b = getBtoBClients()[i];
            if (idStandort === -1 || idStandort === b.i) {
                for (let j = 0; j < b.usr.length; j++) {
                    const userObj = b.usr[j];
                    const id = userObj && userObj.i;
                    const sub = userObj && userObj.s;
                    const name = userObj && [userObj.nn, userObj.vn].join(', ');
                    const valid = userObj && userObj.v;
                    const firma = (b && b.al) || (b && b.n);
                    if (valid && sub !== 900 && !name.includes('CarQR') && !name.includes('ftp')) mitarbeiter.push({ i: sendSub ? sub : id, t: name, group: firma });
                }
            }
        }
    }
    return mitarbeiter;
}

export function getStandorte() {
    return !checkUndefinedNullOrEmpty(gSession.sorte) ? gSession.sorte : [];
}

export function getStandorteForSelect() {
    const standorte = [{ i: 0, t: '(unbekannt)' }];
    for (let i = 0; i < getStandorte().length; i++) {
        const option = getStandorte()[i];
        const key1 = option.i;
        const val1 = option.al ? option.al : option.n;
        standorte.push({ i: key1, t: val1 });
    }
    return standorte;
}

export function getVertretungSeit() {
    return gSession.vertretungSeit;
}

export function setVertretungSeit(value) {
    gSession.vertretungSeit = value;
}

export function getVertretungUser() {
    return gSession.vertretungUser;
}

export function setVertretungUser(value) {
    gSession.vertretungUser = value;
}

export function isNoMailMaster() {
    return gSession.nomailmaster;
}

export function isLeadDisable() {
    return gSession.leadDisable;
}

export function getPrint(selectedOnly: boolean): (Omit<SelectDataObject, 'i'> & { i: number })[] {
    if (!gSession.selPrint) return [];
    else {
        const prints: (Omit<SelectDataObject, 'i'> & { i: number })[] = gSession?.selPrint?.reduce((p, c) => {
            const pId = Object.keys(c)[0];
            if (c?.[pId]?.startsWith('_SSR')) {
                if (!selectedOnly || (selectedOnly === true && c?.selected === true)) {
                    return [
                        ...p,
                        {
                            i: parseInt(pId),
                            t: c?.[pId]?.replace('_SSR', ''),
                        }];
                }
            }

            return p;
        }, [])?.sort((a, b) => a?.t?.toLowerCase()?.localeCompare(b?.t?.toLowerCase()));
        return prints;
    }
}

export function getBulkPrint(selectedOnly: boolean) {
    if (!gSession.selPrint) return [];
    else {
        const ignore = [
            '1521', '1524', '1525', '1537', '1538', '1539', '1540', '1541', '1562', '1567', '1572', '1573', '1574', '1608', '1631', '1632', '1633', '1634', '1635', '1636', '1637', '1638', '1656', '1674', '1675', '1676', '1681', '1682', '1683', '1684',
        ];
        const prints: (Omit<SelectDataObject, 'i'> & { i: number })[] = gSession?.selPrint?.reduce((p, c) => {
            const pId = Object.keys(c)[0];
            if (c?.[pId]?.startsWith('_SSR') && !ignore.includes(pId)) {
                if (!selectedOnly || (selectedOnly === true && c?.selected === true)) {
                    return [
                        ...p,
                        {
                            i: parseInt(pId),
                            t: c?.[pId]?.replace('_SSR', ''),
                        }];
                }
            }

            return p;
        }, [])?.sort((a, b) => a?.t?.toLowerCase()?.localeCompare(b?.t?.toLowerCase()));
        return prints;
    }
}

export function getAktRolle() {
    const m = getMitarbeiter4Id(getIdUser());
    return m && m.ar ? m.ar : 0;
}

export function setAktRolle(aktRole) {
    const m = getMitarbeiter4Id(getIdUser());
    m.ar = aktRole;
}

export function isRolle(key) {
    if (gSession == undefined || gSession.rollen == undefined || gSession.rollen.length == 0) {
        return false;
    }
    for (let i = 0; i < gSession.rollen.length; i++) {
        if (key == gSession.rollen[i]) {
            return true;
        }
    }
    return false;
}

// Array wird zurückgegeben
export function getBtoB(): Session['btob'] {
    return gSession?.btob;
}

export function isBtoB() {
    if (!checkUndefinedNullOrEmpty(gSession?.btob)) {
        return true;
    }
    return false;
}

export function getFirmenForSelect() {
    const firmen = [{ i: getIdFirma(), t: getFirmaAlias() }];
    if (isBtoB()) {
        for (let i = 0; i < getBtoB().length; i++) {
            const option = getBtoB()[i];
            const key1 = option.i;
            const val1 = option.al ? option.al : option.n;
            firmen.push({ i: key1, t: val1 });
        }
    }
    return firmen;
}

export function isBtoBRight(idFirma: Firma['i'], right: string) {
    if (idFirma == getIdFirma()) {
        return true;
    }
    const f = getFirma4Id(idFirma) as ArrayElement<Session['btob']>;
    if (f?.rgt) {
        for (let i = 0; i < f.rgt.length; i++) {
            if (f.rgt[i] == right) {
                return true;
            }
        }
    }
    return false;
}

export function getRightsMap() {
    for (const [profil, mp] of Object.entries(sProfile)) {
        if (!isProfil(getIdUser(), profil as Rolle)) {
            continue;
        }
        // der angemeldete User hat das Profil
        for (const [r, v] of Object.entries(mp)) {
            // in der alten map nachsehen
            const vOld = mViews[r];
            if (!vOld) {
                mViews[r] = v;
            } else if (r > vOld) {
                mViews[r] = v;
            }
        }
    }
}

export function isRightRead(right: keyof Profil) {
    if (checkUndefinedNullOrEmpty(Object.keys(mViews))) {
        getRightsMap();
    }
    const value = mViews[right];
    return value && value > 0;
}

export function getIdTel() {
    if (gSession != undefined || gSession.btob != undefined) {
        return gSession.idTel;
    }
    return undefined;
}

// Array wird zurückgegeben
export function getBtoBClients() {
    if (gSession != undefined || gSession.btob_clients != undefined) {
        return gSession.btob_clients;
    }
    return undefined;
}

export function getMitarbeiter4Id(idUser: FirmaUser['i']): FirmaUser {
    if (gSession == undefined || gSession.firma == undefined || gSession.firma.usr == undefined) {
        return undefined;
    }
    for (let i = 0; i < gSession.firma.usr.length; i++) {
        const m = gSession.firma.usr[i];
        if (idUser == m.i) {
            return m;
        }
    }
    // B2B prüfen
    if (getBtoBClients()) {
        for (let i = 0; i < getBtoBClients().length; i++) {
            const b = getBtoBClients()[i];
            for (let j = 0; j < b.usr.length; j++) {
                const m = b.usr[j];
                if (idUser == m.i) {
                    return m;
                }
            }
        }
    }
    return undefined;
}

export function getMitarbeiter4FirmaId(idFirma, idUser) {
    if (parseInt(idFirma) === getIdFirma()) {
        const firma = getFirma();
        for (let i = 0; i < firma.usr.length; i++) {
            const user = firma.usr[i];
            if (parseInt(idUser) === user.i) {
                return { vn: user.vn, nn: user.nn };
            }
        }
    } else {
        for (let i = 0; i < getBtoBClients().length; i++) {
            const firma = getBtoBClients()[i];
            for (let j = 0; j < firma.usr.length; j++) {
                const user = firma.usr[j];
                if (parseInt(idUser) === user.i) {
                    return { vn: user.vn, nn: user.nn };
                }
            }
        }
    }
    return undefined;
}

export function getMitarbeiter4Sub(idSub) {
    if (gSession == undefined || gSession.firma == undefined || gSession.firma.usr == undefined) {
        return undefined;
    }
    for (let i = 0; i < gSession.firma.usr.length; i++) {
        const m = gSession.firma.usr[i];
        if (idSub == m.s) {
            return m;
        }
    }
    return undefined;
}

export function getCarQRUser() {
    if (gSession == undefined || gSession.firma == undefined || gSession.firma.usr == undefined) {
        return undefined;
    }
    for (let i = 0; i < gSession.firma.usr.length; i++) {
        const m = gSession.firma.usr[i];
        if (m.vn == 'CarQR') {
            return m;
        }
    }
    return undefined;
}

export function firmenDaten() {
    return gSession.firma;
}

// für Lead uns Support verschiedene Session setzen
export function getSessionName() {
    return !LEAD ? 'gsS' : 'gsL';
}

export function getTokenName() {
    return !LEAD ? 'tS' : 're';
}

export function isUsageStandzeiten() {
    if (undefined != getFirma() && undefined != getFirma().cfg) {
        for (let i = 0; i < getFirma().cfg.length; i++) {
            if (getFirma().cfg[i].k == 'allgUseStandzeiten') {
                return getFirma().cfg[i].v;
            }
        }
    }
    return undefined;
}

export function isBewertung(vertrage) {
    if (isTester()) return true;
    else if (11670 === getIdFirma()) return true;
    else if (48831 === getIdFirma()) return true;
    else if (48921 === getIdFirma()) return true;
    else if (49152 === getIdFirma()) return true;
    else return getVertrag('inz', vertrage);
}

// TODO: mit ID versehen, die Überprüfung zu Ende schreiben
export function isFeatureKundenmanagement() {
    return false;
}

export function isAIPreisschilder() {
    return getActivePortals().includes(299);
}

export function getCallNumber(str) {
    str = str.split('+49').join('0');
    str = str.split(' ').join('');
    return str;
}

export function isZusammenschaltung() {
    return gSession.gLoadGruppe;
}

export function isSaveCar(car: Car, soldAllowed: boolean) {
    if (!isVKL() && !isDisposition()) return false;
    if (checkUndefinedNullOrEmpty(car)) return false;
    if (!soldAllowed && !checkUndefinedNullOrEmpty(car?.dv) && car?.dv > 0) return false;
    if (checkUndefinedNullOrEmpty(car?.se)) return false;
    if (car?.se?.i !== getIdFirma() && !isZusammenschaltung()) return false;

    return true;
}

export function allowDAT() {
    return gSession.allowDAT;
}
